import type { ModuleSession } from "@/types"

export interface RegisteredModule {
  id: number
  accessible?: boolean
  sessions?: ModuleSession[]
  join_starts_at?: string | null
  join_ends_at?: string | null
}

export function moduleRegistrationLabel(registered: number, total: number) {
  return total > 0 && registered >= total
    ? "All modules registered"
    : `Registered · ${registered} of ${total} modules`
}

export function nextRegisteredModule<T extends RegisteredModule>(
  modules: T[],
  now = Date.now(),
) {
  // Keep a weekend module selected through breaks until its final class ends.
  return modules
    .filter((module) => module.accessible)
    .map((module) => ({
      module,
      start:
        Date.parse(module.join_starts_at ?? "") ||
        Math.min(
          ...(module.sessions ?? [])
            .map((session) => Date.parse(session.starts_at ?? ""))
            .filter(Number.isFinite),
        ),
      end:
        Date.parse(module.join_ends_at ?? "") ||
        Math.max(
          ...(module.sessions ?? [])
            .map((session) => Date.parse(session.ends_at ?? ""))
            .filter(Number.isFinite),
        ),
    }))
    .filter(({ start, end }) => Number.isFinite(start) && end > now)
    .sort((a, b) => a.start - b.start || a.module.id - b.module.id)[0]?.module
}

export function nextRegisteredClass(
  modules: RegisteredModule[],
  now = Date.now(),
) {
  const nearestModule = nextRegisteredModule(modules, now)

  return nearestModule?.sessions
    ?.map((session) => ({ module: nearestModule, session }))
    .filter(({ session }) => {
      const start = Date.parse(session.starts_at ?? "")
      const end = Date.parse(
        session.ends_at ?? nearestModule.join_ends_at ?? "",
      )
      return Number.isFinite(start) && end > now
    })
    .sort(
      (a, b) =>
        Date.parse(a.session.starts_at!) - Date.parse(b.session.starts_at!),
    )[0]
}

export function shortModuleDate(date: string) {
  return new Date(`${date}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}
